import { TranslateService } from '@ngx-translate/core';
import { ConfigInstanceToken, ConfigInterface } from '../../interfaces/config.interface';
import * as _ from 'lodash';
import moment from 'moment';
import { ConfigConstants } from '../../config/config-constants';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { ClientManagerInstanceToken } from '../../interfaces/client-manager.interface';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { MaterialUtilitiesService } from '../../services/material-utilities/material-utilities.service';
import { ModelOptionsLoaderService } from '../../services/model-options-loader/model-options-loader.service';
import { UriUtilitiesService } from '../../services/uri-utilities/uri-utilities.service';
import { ValueHelperInstanceToken, ValueHelperInterface } from '../../interfaces/value-helper.interface';
import { compact } from '../../utilities/compact';
import { NumberUtilities } from '../../utilities/number-utilities';
import { ODataUtilities } from '../../utilities/odata-utilities';
import { AddressModel, AddressModelFromJson } from '../complex/address.model';
import { CompositeModel } from '../complex/composite.model';
import { CseDopModel, CseDopModelFromJson, JsonCseDopModel } from '../complex/cse-dop.model';
import { DropdownModel } from '../complex/dropdown.model';
import { PartModel } from '../complex/part.model';
import { RadioModel } from '../complex/radio.model';
import { DropdownModelOptions } from '../options/dropdown-model-options';
import { RadioModelOptions } from '../options/radio-model-options';
import { BooleanModel } from '../primitive/boolean.model';
import { DateModel } from '../primitive/date.model';
import { NumberModel } from '../primitive/number.model';
import { TextModel } from '../primitive/text.model';
import { MaterialCheckModel } from './material-check.model';
import {
    JsonMaterialIdentifierModel,
    MaterialIdentifierModel,
    MaterialIdentifierModelFromJson,
    ODataMaterialIdentifierOutModel,
} from './material-identifier.model';
import { MATERIAL_SOURCE_CODE, MaterialSourceModel } from './material-source.model';
import { MATERIAL_STATUS_CODE, MaterialStatusModel } from './material-status.model';
import {
    JsonNotificationIdentifierModel,
    NotificationIdentifierModel,
    NotificationIdentifierModelFromJson,
} from './notification-identifier.model';
import { NotificationModel } from './notification.model';
import {
    AWB_RETURN_FIELD_NAME,
    CONSUME_CODE,
    RETURN_CODE,
    RETURN_REASON_CODE,
    SERIAL_P_INDICATOR,
    TUBE_INDICATOR,
} from './material.enum';
import { isDefined } from '../../utilities/is-defined';
import { Fda2579FormModel, Fda2579FormModelFromJson, JsonFda2579FormModel } from '../complex/fda2579-form.model';
import { EncodingUtilities } from '../../utilities/encoding-utilities';
import { AddressState } from '../../state/address';

export class MaterialModel extends CompositeModel {
    public materialIdentifier: MaterialIdentifierModel;
    public notificationIdentifier: NotificationIdentifierModel;
    public customerAddress: AddressModel;
    public materialKey: string;
    public quantity: NumberModel;
    public quantityUnit: TextModel;
    public faultDescription: TextModel;
    public materialNumberOld: TextModel;
    public originalSerialNumberOld = '';
    public serialNumberOld: TextModel | undefined;
    public originalSerialNumberNew = '';
    public serialNumberNew: TextModel;
    public revisionNumberOld: TextModel;
    public revisionNumberNew: TextModel;
    public installationDateOld: DateModel;
    public installationDateNew: DateModel;
    public counterNumberOld: NumberModel;
    public counterNumberNew: NumberModel;
    public materialStatus: MaterialStatusModel;
    public materialSource: MaterialSourceModel;
    public pickedQuantity: NumberModel;
    public awbReturn: TextModel;
    public storageLocation: TextModel;
    public storageLocationDesc: TextModel;
    public materialSourceText: TextModel;
    public description: TextModel;
    public maxQuantity: number;
    public deliveredQuantity: number;
    public tubeIndicator: string;
    public serialPIndicator: string;
    public showDangerousInfo: boolean;
    public dangerousGoodsIndicator: string;
    public batchNumber: TextModel;
    public awbDelivery: string;
    public ucrNumber: string;
    public unNumber: string;
    public dangerousGoodsText: string;
    public hazardousGoodsText: string;
    public lsCrChangeable: string;
    public localScrapReturnConsumed: string;
    public localScrapReturnWithoutConsumption: string;
    public localScrapReturnLeftOnNotification: string;
    public localScrapReturnNotReceived: string;
    public isBootStockScrapable: boolean;
    public amount: NumberModel;
    public repairPrice: number;
    public currency: string;
    public customerPrice: number;
    public customerCurrency: string;
    public textLine: string;
    public csmlTrackerUrl: string;
    public smartSensorTrackerUrl: string;
    public isRepairable: boolean;
    public isQualityPart: boolean;
    public batchNumberLegacy = '';
    public spqDocId: string;
    public isSpqSubmitted = false;

    public consumptionCode: DropdownModel<TextModel>;
    public serialNumberDropdown: DropdownModel<TextModel> | undefined;
    public billableCode: DropdownModel<TextModel>;
    public phiDataCode: DropdownModel<TextModel>;
    public faultCode: DropdownModel<TextModel>;
    public returnType: DropdownModel<TextModel>;
    public returnReason: DropdownModel<TextModel>;
    public isComplaint: RadioModel<TextModel>;
    public isBatteryRemoved: RadioModel<TextModel>;
    public isBatteryDamaged: RadioModel<TextModel>;
    public doNotReplenishBootstockMaterial: DisplayDecorator<BooleanModel>;
    public isBootstockReplenishEnabled: boolean;
    public isTrunkStockMaterial: boolean;
    public isBootstockReturn = false;
    /**
     * Cached flag indicating if the material is from bootstock source.
     * This is computed from materialSource.sourceCode and cached to avoid
     * repeated checks in templates during change detection cycles.
     */
    public isBootstockMaterial = false;

    public activityDate: DateModel;
    public statusString: TextModel;
    public isCollapsed = true;
    public isConsumed = false;
    public parentMaterialModelIndex = -1;
    public showOnNotificationDetail = true;
    public isPurchaseOrderTaxiBillable = false;
    public isMaterialConsumptionAdditionalTaxiCost = false;

    // Dangerous Goods Info
    public objInfoDGPText: string;

    // Return Model
    public dopAddress: CseDopModel;
    public deliveryReturnSelectedOption: string;
    public dropOffPointSelectedOption: string;
    public returnInfoText: string;
    public awbPackagingText: string;
    public noPhysicalReturn = false;
    public isRowSelected: DisplayDecorator<BooleanModel>;
    public materialReturnedDate: string;

    public isActive: boolean;
    public isRentalTool: boolean;

    public billing: ConfigConstants.BILLABLE_CODE;
    public billingChangeable: boolean;

    // FDA 2579 form
    public isFDA2579FormEnabled = false;
    public fda2579FormModel: Fda2579FormModel;
    public isMedPhysicsRelevant = false;

    // dependencies
    public configService: ConfigInterface;
    public valueHelperService: ValueHelperInterface;
    public modelOptionsLoader: ModelOptionsLoaderService;
    public translate: TranslateService;
    private readonly materialUtilities: MaterialUtilitiesService;

    public orderPriority = '';
    public orderInfoText = '';

    public readonly uriUtilities: UriUtilitiesService;
    // Timestamp when saving the MaterialModel to IndexedDB or concurrenct usage store.
    // It is a utc number given by Date.now() or moment().valueOf().
    public lastSavedTimestamp: number;
    public constructor(
        part: PartModel | undefined,
        private notification: NotificationModel | undefined,
        private complaintText = '',
    ) {
        super('MaterialModel');

        const injector = CustomInjector.getInjector();
        this.uriUtilities = injector.get(UriUtilitiesService);
        this.configService = injector.get(ConfigInstanceToken);
        this.valueHelperService = injector.get(ValueHelperInstanceToken);
        this.modelOptionsLoader = injector.get(ModelOptionsLoaderService);
        this.translate = injector.get(TranslateService);
        const clientManager = injector.get(ClientManagerInstanceToken);
        this.materialUtilities = injector.get(MaterialUtilitiesService);

        this.isBootstockReplenishEnabled = this.configService.getBootstockReplenishEnabled();
        this.materialKey = `${moment.now()}-${NumberUtilities.getRandomNumberBetweenMinAndMax()}`;
        this.activityDate = new DateModel('ActivityDate');
        this.statusString = new TextModel('StatusString');
        if (notification) {
            this.notificationIdentifier = notification.identifier;
            // find the DGP info from the ObjInfo Long text
            const dgpInfoList = notification.objectInformationLongInfo.filter((longInfo) => longInfo.TextID === 'DGP');
            // create the text for the more info pop-up
            // new requirement from SHCNGMP58-376 that we need to show the complete text so we will take out substring method
            this.objInfoDGPText = '';

            for (const dgpInfo of dgpInfoList) {
                this.objInfoDGPText += dgpInfo.TextLine + ' ';
            }
        }

        // Enabling part to be null as in boot stock and other stock we do not get parts
        this.materialIdentifier = part
            ? <MaterialIdentifierModel>this.set(part.materialIdentifier)
            : new MaterialIdentifierModel('', '', '');
        this.isPurchaseOrderTaxiBillable =
            this.notification && this.materialIdentifier.orderNumber.value
                ? this.isMaterialTaxiBillable(this.notification, this.materialIdentifier.orderNumber.value.toString())
                : false;
        this.description = <TextModel>this.set(new TextModel('Matbez', part ? part.descText.value : ''));
        this.deliveredQuantity = part && part.quantity.value ? part.quantity.value : NaN;

        this.tubeIndicator = part ? part.tubeIndicator.value : '';
        this.serialPIndicator = part ? part.serialPIndicator.value : '';
        this.dangerousGoodsIndicator = part ? part.dangerousGoodsIndicator.value : '';
        this.showDangerousInfo =
            typeof part !== 'undefined' &&
            this.configService.getDangerousGoodsIndicator(part.dangerousGoodsIndicator.value);
        this.batchNumber = <TextModel>this.set(new TextModel('BatchNumber', part ? part.batchNumber.value : ''));
        this.awbDelivery = part ? part.awbnr.value : '';
        this.ucrNumber = part?.ucrNumber?.value ?? '';
        this.unNumber = part ? part.unNumber : '';
        this.dangerousGoodsText =
            part && (part.dangerousGoodsText.value || part.dangerousGoodsIndicator.value)
                ? part.dangerousGoodsIndicator.value + ' - ' + part.dangerousGoodsText.value
                : '';
        this.hazardousGoodsText =
            part && (part.hazardousMaterialText.value || part.hazardousMaterial.value)
                ? part.hazardousMaterial.value + ' - ' + part.hazardousMaterialText.value
                : '';
        this.lsCrChangeable = part ? part.lsCrChangeable.value : '';
        this.localScrapReturnConsumed = part ? part.localScrapReturnC.value : '';
        this.localScrapReturnWithoutConsumption = part ? part.localScrapReturnD.value : '';
        this.localScrapReturnLeftOnNotification = part ? part.localScrapReturnV.value : '';
        this.localScrapReturnNotReceived = part ? part.localScrapReturnX.value : '';
        const bootStockScrapEnabled = this.configService.getEnableBootstockScrap();
        this.isBootStockScrapable =
            bootStockScrapEnabled && part?.localScrapReturnD.value === ConfigConstants.bootStockScrapCode;
        this.amount = <NumberModel>this.set(new NumberModel('Preis', part ? part.amount.value : 0));
        this.repairPrice = part && part.repairPrice?.value ? part.repairPrice.value : 0;
        this.currency = part ? part.currency.value : '';
        this.customerPrice = part && part.customerPrice?.value ? part.customerPrice.value : 0;
        this.customerCurrency = part ? part.customerCurrency.value : '';
        this.textLine = part ? part.textLine : '';
        this.spqDocId = part?.spqDocID ?? '';

        this.quantity = <NumberModel>this.set(new NumberModel('Quantity', part ? part.quantity.value : 0));
        this.modelOptionsLoader.load(this.quantity, 'MaterialConsumption', 'Quantity');
        this.maxQuantity = this.quantity.value ? this.quantity.value : NaN;
        this.quantity.options = {
            ...this.quantity.options,
            maxValue: this.maxQuantity,
        };

        this.quantityUnit = <TextModel>this.set(new TextModel('UOM', part ? part.extUnit.value : ''));
        this.faultDescription = <TextModel>this.set(new TextModel('ShortText'));
        this.modelOptionsLoader.load(this.faultDescription, 'MaterialConsumption', 'FaultDescription');

        const process = notification ? notification.processIndicator : ConfigConstants.PROCESS_INDICATOR.INVIVO;
        this.materialNumberOld = <TextModel>this.set(new TextModel('OldMaterial'));
        this.modelOptionsLoader.load(this.materialNumberOld, 'MaterialConsumption', 'MaterialOld', process);
        this.materialNumberOld.options = {
            ...this.materialNumberOld.options,
            applicationIdentifier: ConfigConstants.ApplicationIdentifier.ProductIdentifier,
        };

        this.revisionNumberOld = <TextModel>this.set(new TextModel('MaterialErrorCode'));
        this.modelOptionsLoader.load(this.revisionNumberOld, 'MaterialConsumption', 'RevisionOld');

        this.revisionNumberNew = <TextModel>this.set(new TextModel('RevisionNeu'));
        this.modelOptionsLoader.load(this.revisionNumberNew, 'MaterialConsumption', 'RevisionNew');

        // instDate old should be empty, so its initial value has to be null
        const now = moment().startOf('days');
        this.installationDateOld = <DateModel>this.set(new DateModel('OperationDate', undefined));
        this.modelOptionsLoader.load(this.installationDateOld, 'MaterialConsumption', 'InstDateOld');
        this.installationDateOld.options = {
            ...this.installationDateOld.options,
            maxValue: now.clone(),
        };

        this.installationDateNew = <DateModel>this.set(new DateModel('DefectTubeRemovalDate', now.clone()));
        this.modelOptionsLoader.load(this.installationDateNew, 'MaterialConsumption', 'InstDateNew');
        this.installationDateNew.options = {
            ...this.installationDateNew.options,
            maxValue: now.clone(),
        };

        this.counterNumberOld = <NumberModel>this.set(new NumberModel('CounterX', undefined));
        this.modelOptionsLoader.load(this.counterNumberOld, 'MaterialConsumption', 'CounterOld');

        this.counterNumberNew = <NumberModel>this.set(new NumberModel('Counter', undefined));
        this.modelOptionsLoader.load(this.counterNumberNew, 'MaterialConsumption', 'CounterNew');

        this.pickedQuantity = new NumberModel('PickedQuantity', 1);

        this.phiDataCode = new DropdownModel<TextModel>(
            'Datenträger',
            this.valueHelperService.getMaterialPhiDataCode(),
        );
        this.modelOptionsLoader.load(this.phiDataCode, 'MaterialConsumption', 'PHIData');

        const billableEntries = this.valueHelperService.getBillable();
        this.billableCode = <DropdownModel<TextModel>>(
            this.set(new DropdownModel<TextModel>('Billable', billableEntries))
        );
        this.modelOptionsLoader.load(this.billableCode, 'MaterialConsumption', 'Billable');

        if (part) {
            this.billing = part.billing;
            this.billingChangeable = part.billingChangeable;
            this.setBillableCode();
        }

        const maxAllowedSelection = this.configService.getMaterialErrorCodeCountMax();
        this.faultCode = new DropdownModel<TextModel>(
            'MaterialBugs',
            this.getFaultCodeOptions(),
            new DropdownModelOptions({ maxSelections: maxAllowedSelection }),
        );
        this.modelOptionsLoader.load(this.faultCode, 'MaterialConsumption', 'FaultCode');

        this.returnType = <DropdownModel<TextModel>>(
            this.set(new DropdownModel<TextModel>('LsCrIndicator', this.valueHelperService.getReturnType()))
        );
        this.modelOptionsLoader.load(this.returnType, 'MaterialConsumption', 'ReturnType');
        this.returnReason = <DropdownModel<TextModel>>(
            this.set(new DropdownModel<TextModel>('ReasonCode', this.valueHelperService.getMaterialReturnCause()))
        );
        this.modelOptionsLoader.load(this.returnReason, 'MaterialConsumption', 'ReturnReason');

        const complaintNameAttr = `IsComplaintPart${Math.random()}`;
        this.isComplaint = new RadioModel<TextModel>(
            complaintNameAttr,
            this.getComplaintOptions(),
            new RadioModelOptions({
                initialSelection: ConfigConstants.confirmationComplaintsInitialSelection,
                invalidSelection: 2,
            }),
        );
        this.isBatteryRemoved = new RadioModel<TextModel>(
            this.materialKey,
            this.getBatteryRadioOptions(),
            new RadioModelOptions({ initialSelection: 2, invalidSelection: 2 }),
        );

        this.loadAWBReturnFieldProperties();

        this.isBatteryDamaged = new RadioModel<TextModel>(
            this.materialKey,
            this.getBatteryRadioOptions(),
            new RadioModelOptions({ initialSelection: 2, invalidSelection: 2 }),
        );

        this.materialStatus = new MaterialStatusModel(MATERIAL_STATUS_CODE.ORDERED);
        this.materialSource = new MaterialSourceModel(MATERIAL_SOURCE_CODE.ORDERED);
        this.materialSourceText = <TextModel>this.set(new TextModel('MaterialSource', this.materialSource.sourceCode));
        this.storageLocation = <TextModel>(
            this.set(new TextModel('StorageLocation', part ? part.storageLocation.value : ''))
        );
        this.storageLocationDesc = new TextModel('StorageLocationDesc', part ? part.storageLocationDesc.value : '');
        this.isCollapsed = true;
        this.isActive = true;

        this.isRowSelected = new DisplayDecorator('', new BooleanModel('IsRowSelected', false));

        this.materialReturnedDate = moment().format(clientManager.getDateFormat());

        this.isRentalTool = this.isRentalMaterial();
        this.setRentalToolOptions();

        if (part) {
            this.originalSerialNumberOld = part.serialNumberOld.value ?? '';
            this.originalSerialNumberNew = part.serialNumberNew.value ?? '';
            this.batchNumberLegacy = part.batchNumberLegacy ?? '';
        }
        this.serialNumberNew = <TextModel>(
            this.set(new TextModel('SerialNumber', part ? this.originalSerialNumberNew : ''))
        );
        this.modelOptionsLoader.load(this.serialNumberNew, 'MaterialConsumption', 'SerialNumberNew');
        this.serialNumberNew.options = {
            ...this.serialNumberNew.options,
            applicationIdentifier: ConfigConstants.ApplicationIdentifier.SerialNumber,
        };
        this.setSerialNumberOldField();

        this.setConsumptionCodes();

        if (notification) {
            this.customerAddress = notification.address;
        }
        const csmlTrackerLink = this.configService.getCSMLTrackerLink();
        const useCsmlUrlWithParam = this.configService.getUseCsmlUrlWithParam();
        this.csmlTrackerUrl = this.uriUtilities.getCSMLTrackerURL(
            [this.materialIdentifier],
            csmlTrackerLink,
            true,
            useCsmlUrlWithParam,
        );
        this.smartSensorTrackerUrl = this.hasSmartSensorTracker() ? this.configService.getSmartSensorTrackerUrl() : '';

        this.isRepairable = typeof part !== 'undefined' ? part.repairable : false;

        this.isTrunkStockMaterial = part?.isInBootstock ?? false;

        this.doNotReplenishBootstockMaterial = new DisplayDecorator(
            '',
            new BooleanModel('DoNotReplenishBootStockMaterial', false),
        );

        this.isQualityPart = part?.qualityPart === ConfigConstants.materialQualityPartIndicator;
        this.isMedPhysicsRelevant = part?.isMedPhysicsRelevant ?? false;

        this.lastSavedTimestamp = 0;
        if (part && notification && part.enableFDA2579 && this.configService.getEnableFDA2579Form()) {
            this.isFDA2579FormEnabled = part.enableFDA2579;
            this.fda2579FormModel = new Fda2579FormModel(notification);
        }

        // Compute isBootstockMaterial based on the final materialSource.sourceCode value
        this.isBootstockMaterial = this.materialSource.sourceCode === MATERIAL_SOURCE_CODE.BOOTSTOCK;
    }

    // If Billing Indicators are coming from P58, then consider PartSet.Billing and PartSet.BillingChangeable,
    // Otherwise, pre-select "System Determined" and is not disabled
    public setBillableCode(): void {
        let defaultValue = ConfigConstants.BILLABLE_CODE.AUTO;
        if (this.configService.getEnableBillingIndicator()) {
            defaultValue = this.billing;
            // if PartSet.BillingChangeable='N' or PartSet.Billing='', then dropdown should be disabled
            this.billableCode.options = {
                ...this.billableCode.options,
                disabled: !this.billingChangeable || this.billing.length === 0,
                required: this.billingChangeable,
            };
        }
        this.billableCode.selectByPredicate((entry) => entry.value === defaultValue);
    }

    public setReturnTypeOptions(consumeCode: string): void {
        if (this.isLithiumBatteryStandAlone()) {
            this.setReturnTypeOptionsForLithiumBatteryStandAlone();
        } else if (this.isLithiumBatteryInDevice()) {
            this.setReturnTypeOptionsForLithiumBatteryInDevice();
        } else {
            this.setReturnTypeOptionsNGMP58(consumeCode);
        }
        this.loadAWBReturnFieldProperties();

        // load serial number original values only if consumed is selected
        this.materialUtilities.loadSerialNumber(consumeCode, this);
    }

    /**
     * For Rented Material, Fault code,fault description is not mandatory and return reason option should be disabled
     * https://evorait.atlassian.net/browse/SHP-5574
     */
    public setRentalToolOptions(): void {
        if (this.isRentalTool) {
            this.faultCode.options = {
                ...this.faultCode.options,
                required: false,
            };
            this.faultDescription.options = {
                ...this.faultDescription.options,
                required: false,
            };
            this.returnReason.options = {
                ...this.returnReason.options,
                disabled: true,
            };
        }
    }

    private setCentralReturn(): void {
        let returnOptions = this.valueHelperService.getReturnType();
        returnOptions = returnOptions.filter(
            (option: DisplayDecorator<TextModel>) => option.model.value === RETURN_CODE.CENTRAL_RETURN,
        );
        this.returnType.value = returnOptions;
        this.returnType.selectByIndex(0);
    }

    private setLocalScrap(): void {
        this.returnType.value = this.valueHelperService
            .getReturnType()
            .filter((option) => option.model.value === RETURN_CODE.LOCAL_SCRAP);
        this.returnType.selectByIndex(0);
        this.returnReason.deselect();
    }

    /**
     * In different countries there are different forwarder for dangerous goods and non-dangerous goods, hence a config
     * is maintained to indicate whether a country has a forwarder that can handle dangerous goods for Central scrap
     * This method is called during handling of such materials, and based on the config the return type is to be set
     * either to allow both local and central scrap, or allow only local scrap for such materials
     *
     * @param lsCsCrIndicator - LS/CS/CR indicator
     */
    private setScrapOptionsForDangerousGoods(lsCsCrIndicator: string): void {
        if (this.configService.getDisplayCentralScrapForDangerousGoods()) {
            this.returnReason.deselect();
            const returnTypeIndicator =
                lsCsCrIndicator !== RETURN_CODE.CENTRAL_RETURN ? lsCsCrIndicator : RETURN_CODE.LOCAL_SCRAP;
            this.setReturnScrapType(returnTypeIndicator, false);
        } else {
            this.setLocalScrap();
        }
    }

    /**
     * For Invitro Notification,
     * If a material is repairable then central return is selected
     * If a material is non repairable then local scrap is selected
     */
    public setReturnTypeOptionsForNonRepairableMaterial(): void {
        let returnOptions = this.valueHelperService.getReturnType();
        returnOptions = returnOptions.filter(
            (option: DisplayDecorator<TextModel>) =>
                option.model.value === RETURN_CODE.CENTRAL_RETURN || option.model.value === RETURN_CODE.LOCAL_SCRAP,
        );
        this.returnType.value = returnOptions;
        this.setReturnTypeForDeadOnArrivalMaterial();
    }

    private setReturnScrapType(lsCsCrIndicator: string, allowBootStockIndicator: boolean): void {
        // In case of LS/CR/CS indicator = L or S:  It is allowed for the user to change the value for the “return type” between L = local scrap and S = central scrap and vice-versa.
        let returnOptions = this.valueHelperService.getReturnType();
        returnOptions = returnOptions.filter((option) => option.model.value !== RETURN_CODE.CENTRAL_RETURN);
        // For wave1, Central scrap is currently not covered by the process.
        // Hence, this configuration is maintained in the gateway based on which it will be displayed to the user.
        const displayCentralScrap = this.configService.getDisplayCentralScrap();
        if (!allowBootStockIndicator) {
            returnOptions = returnOptions.filter((option) => option.model.value !== RETURN_CODE.BOOT_STOCK);
        }
        if (displayCentralScrap) {
            this.returnType.value = returnOptions;
            this.returnType.selectByValue(lsCsCrIndicator);
        } else {
            returnOptions = returnOptions.filter((option) => option.model.value !== RETURN_CODE.CENTRAL_SCRAP);
            this.returnType.value = returnOptions;

            const isAddToBootStockInReturnType = returnOptions.some(
                (option) => option.model.value === RETURN_CODE.BOOT_STOCK,
            );

            if (returnOptions.length === 1 || !isAddToBootStockInReturnType) {
                this.returnType.selectByIndex(0);
            }
        }
    }

    public setReturnTypeOptionsForLithiumBatteryInDevice(): void {
        this.returnType.deselect();

        const isBatteryRemoved = this.isBatteryRemoved.selectedEntry.value === ConfigConstants.optionValueYes;
        const isBatteryNotRemoved = this.isBatteryRemoved.selectedEntry.value === ConfigConstants.optionValueNo;
        const isBatteryDamaged = this.isBatteryDamaged.selectedEntry.value === ConfigConstants.optionValueYes;
        const isBatteryNotDamaged = this.isBatteryDamaged.selectedEntry.value === ConfigConstants.optionValueNo;

        if (this.consumptionCode.selectedEntry?.value === CONSUME_CODE.CONSUMED) {
            // If battery is removed, or battery is undamaged, then return type is based on backend indicator.
            if (isBatteryRemoved || (isBatteryNotRemoved && isBatteryNotDamaged)) {
                if (this.localScrapReturnConsumed === RETURN_CODE.CENTRAL_RETURN) {
                    this.setCentralReturn();
                } else {
                    this.setReturnScrapType(this.localScrapReturnConsumed, false);
                }
                // If battery is damaged, then material can only be scrapped. Central scrap can be an option based on config.
            } else if (isBatteryDamaged) {
                this.setScrapOptionsForDangerousGoods(this.localScrapReturnConsumed);
            }
        } else if (this.consumptionCode.selectedEntry?.value === CONSUME_CODE.RETURN_WITHOUT_CONSUMPTION) {
            // If backend indicator is for scrap, then central return is not allowed
            // If backend indicator is for return and if battery is damaged,
            // then material can only be scrapped. Central scrap can be an option based on config.
            if (this.localScrapReturnWithoutConsumption !== RETURN_CODE.CENTRAL_RETURN || isBatteryDamaged) {
                this.setScrapOptionsForDangerousGoods(this.localScrapReturnWithoutConsumption);
            } else {
                this.setCentralReturn();
            }
        }
    }

    public setReturnTypeOptionsForLithiumBatteryStandAlone(): void {
        if (this.consumptionCode.selectedEntry?.value === CONSUME_CODE.CONSUMED) {
            this.setLocalScrap();
        }
        if (this.consumptionCode.selectedEntry?.value === CONSUME_CODE.RETURN_WITHOUT_CONSUMPTION) {
            if (
                this.localScrapReturnWithoutConsumption === RETURN_CODE.LOCAL_SCRAP ||
                this.localScrapReturnWithoutConsumption === RETURN_CODE.CENTRAL_SCRAP
            ) {
                this.setLocalScrap();
            } else {
                this.returnType.value = this.valueHelperService
                    .getReturnType()
                    .filter(
                        (option) =>
                            option.model.value === RETURN_CODE.LOCAL_SCRAP ||
                            option.model.value === RETURN_CODE.CENTRAL_RETURN,
                    );
                const selectedReturnReason = this.returnReason.selectedEntry?.value;
                if (
                    selectedReturnReason === RETURN_REASON_CODE.DIAGNOSIS_SEALED ||
                    selectedReturnReason === RETURN_REASON_CODE.DEAD_ON_ARRIVAL ||
                    selectedReturnReason === RETURN_REASON_CODE.TRANSPORTATION_DAMAGE
                ) {
                    this.returnType.selectByPredicate((option) => option.model.value === RETURN_CODE.LOCAL_SCRAP);
                } else {
                    this.returnType.selectByPredicate((option) => option.model.value === RETURN_CODE.CENTRAL_RETURN);
                }
            }
        }
    }

    public setReturnTypeOptionsNGMP58(consumeCode: string): void {
        this.returnType.deselect();
        this.returnType.options = {
            ...this.returnType.options,
            disabled: false,
        };
        // For MK = 'D', i.e Return without Consumption, localScrapReturnD value will be used to set the return types
        if (consumeCode === CONSUME_CODE.RETURN_WITHOUT_CONSUMPTION) {
            // In case of LS/CR/CS indicator = C (central return) the material has to be send back (no change allowed).
            if (
                this.localScrapReturnWithoutConsumption === RETURN_CODE.CENTRAL_RETURN ||
                this.isComplaint.selectedEntry.value === ConfigConstants.optionYes ||
                this.hasSmartSensorTracker()
            ) {
                this.setCentralReturn();
            } else if (
                this.localScrapReturnWithoutConsumption === RETURN_CODE.LOCAL_SCRAP ||
                this.localScrapReturnWithoutConsumption === RETURN_CODE.CENTRAL_SCRAP
            ) {
                // for P58 invitro notifications, we need to display boot stock as an option in return type. The return type indicator value will be 'G'.
                // https://evorait.atlassian.net/browse/SHP-5554
                const displayBootStockIndicator =
                    this.configService.getDisplayBootStockIndicator() &&
                    this.notification?.processIndicator === ConfigConstants.PROCESS_INDICATOR.INVITRO;
                this.setReturnScrapType(this.localScrapReturnWithoutConsumption, displayBootStockIndicator);
            }
        } else if (consumeCode === CONSUME_CODE.CONSUMED) {
            // For MK = 'C' i.e Consumed, localScrapReturnC value will be used to set the return types.
            // In case of LS/CR/CS indicator = C (central return) the material has to be send back (no change allowed).
            // If it is a rental material, then only central return is allowed.
            if (
                this.localScrapReturnConsumed === RETURN_CODE.CENTRAL_RETURN ||
                this.isComplaint.selectedEntry.value === ConfigConstants.optionYes ||
                this.isRentalTool ||
                this.hasSmartSensorTracker()
            ) {
                this.setCentralReturn();
            } else if (this.isMaterialScrapped()) {
                this.setReturnScrapType(this.localScrapReturnConsumed, false);
            }
        }
    }

    public setMaterialTaxiAdditionalCost(value: boolean): void {
        if (this.isPurchaseOrderTaxiBillable) {
            this.isMaterialConsumptionAdditionalTaxiCost = value;
        }
    }

    public getFaultCodeOptions(): Array<DisplayDecorator<TextModel>> {
        let faultOptions = this.valueHelperService.getMaterialBug();
        if (this.tubeIndicator) {
            if (this.tubeIndicator === TUBE_INDICATOR.IMAGE_INTENSIFIER_INDICATOR) {
                faultOptions = this.valueHelperService.getMaterialErrorTubeI();
            } else if (this.tubeIndicator === TUBE_INDICATOR.TUBE_PART_INDICATOR) {
                faultOptions = this.valueHelperService.getMaterialErrorTubeJ();
            }
        }
        return faultOptions;
    }

    public clone(): MaterialModel {
        // pass part as null because part set is not a factory, will not be persisted
        const material = new MaterialModel(undefined, this.notification, this.complaintText);
        return this.materialUtilities.clone(material, this);
    }

    /**
     * Update the selected material information
     * Use case: material details got updated from SAP after the notification has reached to device
     *
     * @param orderedMaterial the material information from backend
     */
    public updateMaterialInfo(orderedMaterial: MaterialModel): void {
        this.repairPrice = orderedMaterial.repairPrice;
        this.currency = orderedMaterial.currency;
        this.customerPrice = orderedMaterial.customerPrice;
        this.customerCurrency = orderedMaterial.customerCurrency;
        this.description.value = orderedMaterial.description.value;
        this.deliveredQuantity = orderedMaterial.deliveredQuantity;
        this.tubeIndicator = orderedMaterial.tubeIndicator;
        this.serialPIndicator = orderedMaterial.serialPIndicator;
        this.dangerousGoodsIndicator = orderedMaterial.dangerousGoodsIndicator;
        this.showDangerousInfo = this.configService.getDangerousGoodsIndicator(orderedMaterial.dangerousGoodsIndicator);
        this.batchNumber.value = orderedMaterial.batchNumber.value;
        this.awbDelivery = orderedMaterial.awbDelivery;
        this.ucrNumber = orderedMaterial.ucrNumber;
        this.unNumber = orderedMaterial.unNumber;
        this.dangerousGoodsText = orderedMaterial.dangerousGoodsText;
        this.hazardousGoodsText = orderedMaterial.hazardousGoodsText;
        this.quantityUnit.value = orderedMaterial.quantityUnit.value;
        this.isActive = orderedMaterial.isActive;
        // Backend can only change serial number of job once.
        // User input Serial Number will always have preference over backend serial number.
        if (this.serialNumberOld && orderedMaterial.serialNumberOld) {
            this.serialNumberOld.value = this.serialNumberOld.value || orderedMaterial.serialNumberOld.value;
        }
        this.serialNumberNew.value = this.serialNumberNew.value || orderedMaterial.serialNumberNew.value;
        this.isRepairable = orderedMaterial.isRepairable;
        this.isQualityPart = orderedMaterial.isQualityPart;
        this.batchNumberLegacy = orderedMaterial.batchNumberLegacy;
        this.spqDocId = orderedMaterial.spqDocId;
        if (this.configService.getEnableBillingIndicator()) {
            // if the billing option is not changeable, then override with backend value
            // if the billing option is changeable now, then override with backend value
            if (
                orderedMaterial.billableCode.options.disabled ||
                (!orderedMaterial.billableCode.options.disabled && this.billableCode.options.disabled)
            ) {
                this.billableCode.selectByPredicate(
                    (option) => option.model.value === orderedMaterial.billableCode.selectedEntry?.value,
                );
                this.billableCode.options = {
                    ...this.billableCode.options,
                    disabled:
                        orderedMaterial.billableCode.options.disabled ||
                        orderedMaterial.billableCode.selectedEntry?.value?.length === 0,
                };
            }
        }
    }

    public resetForReturnWithoutConsumption(): void {
        if (!this.configService.getEnableBillingIndicator()) {
            this.billableCode.deselect();
        }
        if (isDefined(this.serialNumberOld)) {
            this.serialNumberOld.value = '';
        }
        this.serialNumberNew.value = '';
        this.materialNumberOld.value = '';
        this.counterNumberOld.value = NaN;
        this.counterNumberNew.value = NaN;
        this.revisionNumberOld.value = '';
        this.revisionNumberNew.value = '';
        this.quantity.options = {
            ...this.quantity.options,
            readonly: false,
        };
        this.isComplaint.selectByIndex(ConfigConstants.confirmationComplaintsInitialSelection);
        this.phiDataCode.deselect();
        this.serialNumberDropdown?.deselect();
    }

    public resetForLeftOnNotification(): void {
        this.resetForReturnWithoutConsumption();
        this.resetFaultCodeAndText();
        this.returnType.deselect();
        this.returnReason.deselect();
        this.awbReturn.value = '';
        this.installationDateOld.value = moment.invalid();
        this.installationDateNew.value = moment();
    }

    public resetForNotReceived(): void {
        this.resetForLeftOnNotification();
        this.quantity.options = {
            ...this.quantity.options,
            readonly: true,
        };
    }

    public resetFaultCodeAndText(): void {
        this.faultCode.deselect();
        this.faultDescription.value = '';
    }

    public setReturnReasonOptions(): void {
        let returnReasons = this.valueHelperService.getMaterialReturnCause();
        if (this.hasSmartSensorTracker()) {
            returnReasons = returnReasons.filter((option) => option.value === RETURN_REASON_CODE.ORIGINAL_PACKED);
        } else if (this.isRentalTool) {
            returnReasons = returnReasons.filter((option) => option.value === RETURN_REASON_CODE.DIAGNOSIS_SEALED);
        }
        this.returnReason.value = returnReasons;
    }

    public setConsumptionCodes(): void {
        let consumptionCodeOptions = this.valueHelperService.getMaterialMK();

        if (
            this.materialSource.sourceCode === MATERIAL_SOURCE_CODE.ORDERED ||
            this.materialSource.sourceCode === MATERIAL_SOURCE_CODE.SPLIT
        ) {
            if (this.materialStatus.statusCode === MATERIAL_STATUS_CODE.PICKED) {
                consumptionCodeOptions = consumptionCodeOptions.filter(
                    (option) => option.model.value !== CONSUME_CODE.NOT_RECEIVED,
                );
                // If it is a rental material, then RETURN_WITHOUT_CONSUMPTION is not allowed
                if (this.isRentalTool && !this.configService.getRentalToolAllowWithNoConsumption()) {
                    consumptionCodeOptions = consumptionCodeOptions.filter(
                        (option) => option.model.value !== CONSUME_CODE.RETURN_WITHOUT_CONSUMPTION,
                    );
                }
                // If it is a Smart Sensor Tracker, then LEFT_ON_NOTIFICATION & RETURN_WITHOUT_CONSUMPTION are allowed
                if (this.hasSmartSensorTracker()) {
                    consumptionCodeOptions = consumptionCodeOptions.filter(
                        (option) =>
                            option.model.value === CONSUME_CODE.LEFT_ON_NOTIFICATION ||
                            option.model.value === CONSUME_CODE.RETURN_WITHOUT_CONSUMPTION,
                    );
                }
            }
        } else if (
            this.materialSource.sourceCode === MATERIAL_SOURCE_CODE.BOOTSTOCK ||
            this.materialSource.sourceCode === MATERIAL_SOURCE_CODE.OTHERSTOCK
        ) {
            // for boot stock and other stock only one consume code has to be displayed
            consumptionCodeOptions = consumptionCodeOptions.filter(
                (option) => option.model.value === CONSUME_CODE.CONSUMED,
            );
        } else {
            // for additional Material “Consumed” and “Return without consumption” has to be displayed
            consumptionCodeOptions = consumptionCodeOptions.filter(
                (option) =>
                    option.model.value === CONSUME_CODE.CONSUMED ||
                    (this.configService.getDisplayAddMaterialButton() &&
                        option.model.value === CONSUME_CODE.RETURN_WITHOUT_CONSUMPTION),
            );
        }
        this.consumptionCode = <DropdownModel<TextModel>>(
            this.set(new DropdownModel<TextModel>('MKCode', consumptionCodeOptions))
        );
        // clear the default selection
        this.consumptionCode.deselect();
        this.modelOptionsLoader.load(this.consumptionCode, 'MaterialConsumption', 'Consumed');
    }

    /**
     * Only save those value that might have been modified by the user and cannot be restored from some kind of default value
     *
     * @returns the JSON representation of the model
     */
    public toJSON(): unknown {
        let serialNumberDropdown = '';
        if (this.serialNumberDropdown) {
            serialNumberDropdown = this.serialNumberDropdown.options.displayText
                ? this.serialNumberDropdown.textFieldValue
                : this.serialNumberDropdown.selectedEntry.model.value;
        }

        return {
            materialIdentifier: this.materialIdentifier,
            notificationIdentifier: this.notificationIdentifier,
            materialKey: this.materialKey,
            description: this.description.value,
            quantity: this.quantity.value,
            quantityUnit: this.quantityUnit.value,
            maxQuantity: this.maxQuantity,
            faultDescription: this.faultDescription.value,
            awbReturn: this.awbReturn.value,
            materialNumberOld: this.materialNumberOld.value,
            serialNumberOld: this.serialNumberOld?.value,
            serialNumberNew: this.serialNumberNew.value,
            revisionNumberOld: this.revisionNumberOld.value,
            revisionNumberNew: this.revisionNumberNew.value,
            installationDateOld: this.installationDateOld.value,
            installationDateNew: this.installationDateNew.value,
            counterNumberOld: this.counterNumberOld.value,
            counterNumberNew: this.counterNumberNew.value,
            pickedQuantity: this.pickedQuantity.value,
            materialStatus: this.materialStatus.statusCode,
            materialSource: this.materialSource.sourceCode,
            deliveredQuantity: this.deliveredQuantity,
            tubeIndicator: this.tubeIndicator,
            serialPIndicator: this.serialPIndicator,
            dangerousGoodsIndicator: this.dangerousGoodsIndicator,
            showDangerousInfo: this.showDangerousInfo,
            batchNumber: this.batchNumber.value,
            awbDelivery: this.awbDelivery,
            ucrNumber: this.ucrNumber,
            unNumber: this.unNumber,
            dangerousGoodsText: this.dangerousGoodsText,
            hazardousGoodsText: this.hazardousGoodsText,
            parentMaterialModelIndex: this.parentMaterialModelIndex,
            consumptionCode: this.consumptionCode.selectedEntries.map((entry) => {
                return entry.value;
            }),
            phiDataCode: this.phiDataCode.selectedEntries.map((entry) => {
                return entry.value;
            }),
            billableCode: this.billableCode.selectedEntries.map((entry) => {
                return entry.value;
            }),
            billing: this.billing,
            billingChangeable: this.billingChangeable,
            billableCodeDropdownDisabled: this.billableCode.options.disabled,
            billableCodeDropdownRequired: this.billableCode.options.required,
            faultCode: this.faultCode.selectedEntries.map((entry) => {
                return entry.value;
            }),
            returnType: this.returnType.selectedEntries.map((entry) => {
                return entry.value;
            }),
            returnReason: this.returnReason.selectedEntries.map((entry) => {
                return entry.value;
            }),
            storageLocation: this.storageLocation.value,
            activityDate: this.activityDate.value,
            statusString: this.statusString.value,
            isConsumed: this.isConsumed,
            dopAddress: this.dopAddress,
            deliveryReturnSelectedOption: this.deliveryReturnSelectedOption,
            dropOffPointSelectedOption: this.dropOffPointSelectedOption,
            returnInfoText: this.returnInfoText,
            awbPackagingText: this.awbPackagingText,
            isRowSelected: this.isRowSelected.value,
            materialReturnedDate: this.materialReturnedDate,
            noPhysicalReturn: this.noPhysicalReturn,
            showOnNotificationDetail: this.showOnNotificationDetail,
            complaintText: this.isComplaint.selectedEntry.value,
            isBatteryRemoved: this.isBatteryRemoved.selectedEntry.value,
            isBatteryDamaged: this.isBatteryDamaged.selectedEntry.value,
            objInfoDGPText: this.objInfoDGPText,
            lsCrChangeable: this.lsCrChangeable,
            localScrapReturnConsumed: this.localScrapReturnConsumed,
            localScrapReturnWithoutConsumption: this.localScrapReturnWithoutConsumption,
            localScrapReturnLeftOnNotification: this.localScrapReturnLeftOnNotification,
            localScrapReturnNotReceived: this.localScrapReturnNotReceived,
            amount: this.amount.value,
            repairPrice: this.repairPrice,
            currency: this.currency,
            customerPrice: this.customerPrice,
            customerCurrency: this.customerCurrency,
            textLine: this.textLine,
            isActive: this.isActive,
            customerAddress: this.customerAddress,
            orderPriority: this.orderPriority,
            orderInfoText: this.orderInfoText,
            originalSerialNumberOld: this.serialNumberOld?.value || this.originalSerialNumberOld,
            originalSerialNumberNew: this.serialNumberNew.value || this.originalSerialNumberNew,
            isPurchaseOrderTaxiBillable: this.isPurchaseOrderTaxiBillable,
            isMaterialConsumptionAdditionalTaxiCost: this.isMaterialConsumptionAdditionalTaxiCost,
            isRepairable: this.isRepairable,
            isQualityPart: this.isQualityPart,
            batchNumberLegacy: this.batchNumberLegacy,
            spqDocId: this.spqDocId,
            isRentalTool: this.isRentalTool,
            serialNumberOldDropdown: serialNumberDropdown,
            lastSavedTimestamp: this.lastSavedTimestamp,
            isFDAFormEnabled: this.isFDA2579FormEnabled,
            fda2579FormModel: this.fda2579FormModel,
            doNotReplenishBootstockMaterial: this.doNotReplenishBootstockMaterial.value,
            isTrunkStockMaterial: this.isTrunkStockMaterial,
            isBootstockReturn: this.isBootstockReturn,
            isBootStockScrapable: this.isBootStockScrapable,
            isBootstockMaterial: this.isBootstockMaterial,
            isSpqSubmitted: this.isSpqSubmitted,
            isMedPhysicsRelevant: this.isMedPhysicsRelevant,
        };
    }

    public getOutModel(): MaterialOutModel {
        const outModel = <MaterialOutModel>super.getOutModel();

        // For NGMP58 If the isComplaint value is checked add the "Q" to "MaterialBugs"
        const addComplaintPart: string[] =
            this.isComplaint.selectedEntry.value === ConfigConstants.optionYes ? ['Q'] : [];

        // dirty hack: use type assertion because outModel in type system does not allow to use the correct type.
        const faultCodeOutModel = <{ [key: string]: string[] }>this.faultCode.getOutModel();
        _.merge(outModel, ODataUtilities.joinAndAppendArrayOutModel(faultCodeOutModel, addComplaintPart, true));

        if (
            this.configService.getEnableBillingIndicator() &&
            this.consumptionCode.selectedEntry.value !== CONSUME_CODE.CONSUMED
        ) {
            delete (outModel as Omit<MaterialOutModel, 'Billable'>).Billable;
        }

        let materialSource = '';

        if (this.materialSource.sourceCode === MATERIAL_SOURCE_CODE.BOOTSTOCK) {
            materialSource = 'B';
        } else if (this.materialSource.sourceCode === MATERIAL_SOURCE_CODE.OTHERSTOCK) {
            materialSource = 'O';
        }
        outModel.MaterialSource = materialSource;
        _.merge(outModel, this.materialIdentifier.getOutModel()); // orderNumber, orderPosition, materialNumber

        // convert numbers to strings as expected by gateway
        outModel.Quantity = outModel.Quantity ? outModel.Quantity.toString() : '';
        outModel.Counter = outModel.Counter ? outModel.Counter.toString() : '';
        outModel.CounterX = outModel.CounterX ? outModel.CounterX.toString() : '';
        outModel.RevisionNeu = outModel.RevisionNeu ? outModel.RevisionNeu.toString() : '';
        delete (outModel as Omit<MaterialOutModel, 'Preis'>).Preis;

        // Initialize with "false" so that if feature is switched off, no purchase order is created.
        // Also for any bootstock material not having "isTrunkStockMaterial", this should never become an X.
        outModel.Herkunft = ConfigConstants.sapFalse;
        if (
            this.isBootstockReplenishEnabled &&
            !this.doNotReplenishBootstockMaterial.value &&
            this.isTrunkStockMaterial
        ) {
            outModel.Herkunft = ConfigConstants.sapTrue;
        }

        if (this.isFDA2579FormEnabled) {
            outModel.FDA2579Form =
                this.consumptionCode.selectedEntry?.value === CONSUME_CODE.CONSUMED
                    ? EncodingUtilities.base64EncodeUnicode(JSON.stringify(this.fda2579FormModel.getOutModel()))
                    : '';
        }

        return outModel;
    }

    public setSerialNumberOldField(): void {
        if (typeof this.notification === 'undefined') {
            return;
        }
        const oldSerialNumberList = this.isRentalTool
            ? []
            : this.materialUtilities.getOldSerialNumberList(this, this.notification);
        this.serialNumberDropdown = <DropdownModel<TextModel>>(
            this.set(new DropdownModel<TextModel>('OldSerialNumber', oldSerialNumberList))
        );
        this.modelOptionsLoader.load(this.serialNumberDropdown, 'MaterialConsumption', 'SerialNumberOld');
        this.serialNumberDropdown.options = {
            ...this.serialNumberDropdown.options,
            displayText: true,
        };
        if (this.isRentalTool) {
            this.serialNumberDropdown.textFieldValue = this.originalSerialNumberNew;
            this.serialNumberNew.value = this.originalSerialNumberNew;
        }
    }

    private getComplaintOptions(): Array<DisplayDecorator<TextModel>> {
        const complaintOptionGroup = [];
        complaintOptionGroup.push(
            new DisplayDecorator(
                <string>this.translate.instant('CONFIRMATION_COMPLAINTS_OPTION_YES'),
                new TextModel(ConfigConstants.optionYes, ConfigConstants.optionYes),
            ),
        );
        complaintOptionGroup.push(
            new DisplayDecorator(
                <string>this.translate.instant('CONFIRMATION_COMPLAINTS_OPTION_NO'),
                new TextModel(ConfigConstants.optionNo, ConfigConstants.optionNo),
            ),
        );
        complaintOptionGroup.push(
            new DisplayDecorator(
                <string>this.translate.instant('CONFIRMATION_COMPLAINTS_OPTION_NONE'),
                new TextModel(ConfigConstants.optionNA, ConfigConstants.optionNA),
            ),
        );

        return complaintOptionGroup;
    }

    private getBatteryRadioOptions(): Array<DisplayDecorator<TextModel>> {
        const batteryRemovedOptionGroup = [];
        batteryRemovedOptionGroup.push(
            new DisplayDecorator(
                <string>this.translate.instant('CONFIRMATION_MATERIAL_RADIO_OPTION_YES'),
                new TextModel(ConfigConstants.optionYes, ConfigConstants.optionValueYes),
            ),
        );
        batteryRemovedOptionGroup.push(
            new DisplayDecorator(
                <string>this.translate.instant('CONFIRMATION_MATERIAL_RADIO_OPTION_NO'),
                new TextModel(ConfigConstants.optionNo, ConfigConstants.optionValueNo),
            ),
        );
        batteryRemovedOptionGroup.push(
            new DisplayDecorator(
                <string>this.translate.instant('CONFIRMATION_MATERIAL_RADIO_OPTION_NONE'),
                new TextModel(ConfigConstants.optionNA, ConfigConstants.optionValueNA),
            ),
        );

        return batteryRemovedOptionGroup;
    }

    /**
     * For NGMP58:
     * SHCNGMP58-1195: Different Awb return barcode patterns are used for Central Return and Central Scrap
     * SHCNGMP58-1815: Different Awb return barcode patterns are used for Material with special Dangerous Goods Indicator
     */
    public loadAWBReturnFieldProperties(): void {
        this.awbReturn = <TextModel>this.set(new TextModel('AirwayBillNo', this.awbReturn?.value));
        if (this.returnType.selectedEntry) {
            const process = this.notification
                ? this.notification.processIndicator
                : ConfigConstants.PROCESS_INDICATOR.INVIVO;
            const selectedReturnType = this.returnType.selectedEntry.value;
            if (selectedReturnType !== RETURN_CODE.CENTRAL_RETURN && selectedReturnType !== RETURN_CODE.CENTRAL_SCRAP) {
                this.awbReturn.value = '';
                return;
            }
            if (this.materialUtilities.isSpecialDangerousGoodsReturn(this)) {
                this.modelOptionsLoader.load(
                    this.awbReturn,
                    'MaterialConsumption',
                    AWB_RETURN_FIELD_NAME.DANGEROUS_GOODS,
                    process,
                );
                if (this.configService.getDisableSpecialTransportAWBfield()) {
                    this.awbReturn.options = {
                        ...this.awbReturn.options,
                        readonly: true,
                        placeholder: 'Special Transport',
                    };
                }
                return;
            }
            const fieldName =
                selectedReturnType === RETURN_CODE.CENTRAL_RETURN
                    ? AWB_RETURN_FIELD_NAME.CENTRAL_RETURN
                    : AWB_RETURN_FIELD_NAME.CENTRAL_SCRAP;
            this.modelOptionsLoader.load(this.awbReturn, 'MaterialConsumption', fieldName, process);
        } else {
            this.modelOptionsLoader.load(this.awbReturn, 'MaterialConsumption', AWB_RETURN_FIELD_NAME.DEFAULT);
        }
    }

    public static fromMaterialCheckModel(
        materialCheckModel: MaterialCheckModel,
        notificationModel: NotificationModel,
    ): MaterialModel {
        const part: PartModel = PartModel.build({});
        part.materialIdentifier.materialNumber = materialCheckModel.materialNr;
        part.quantity.value = materialCheckModel.quantity.value;
        part.storageLocation.value = materialCheckModel.storageLoc.value;
        part.descText.value = materialCheckModel.descTxt;
        part.amount.value = materialCheckModel.customerPrice;
        part.currency.value = materialCheckModel.customerCurrency;
        part.repairPrice.value = materialCheckModel.repairPrice;
        part.storageLocationDesc.value = materialCheckModel.storageLocDesc;
        return new MaterialModel(part, notificationModel);
    }

    protected isMaterialTaxiBillable(notification: NotificationModel, orderNumber: string): boolean {
        if (notification && notification.taxiLongInfo) {
            // Regex to extract number from string
            const regex = new RegExp('\\d+', 'g');
            return notification.taxiLongInfo.some((taxiInfo) =>
                compact(taxiInfo.TextLine.match(regex)).some((num) => num === orderNumber),
            );
        }
        return false;
    }

    protected isRentalMaterial(): boolean {
        if (this.notification && this.notification.rentedMaterials) {
            return this.notification.rentedMaterials.some(
                (rentMaterial) => rentMaterial.TextLine === this.materialIdentifier.materialNumber.value,
            );
        }
        return false;
    }

    public showFDA2579Form(): boolean {
        return this.isFDA2579FormEnabled && this.consumptionCode.selectedEntry?.model.value === CONSUME_CODE.CONSUMED;
    }

    public setReturnTypeForDeadOnArrivalMaterial(): void {
        if (this.returnReason.selectedEntry?.model.value === RETURN_REASON_CODE.DEAD_ON_ARRIVAL) {
            this.returnType.selectByValue(RETURN_CODE.LOCAL_SCRAP);
        } else {
            this.returnType.selectByValue(RETURN_CODE.CENTRAL_RETURN);
            this.loadAWBReturnFieldProperties();
        }
    }

    public isMaterialSerialized(): boolean {
        return this.serialPIndicator === SERIAL_P_INDICATOR.J_INDICATOR;
    }

    public isMaterialScrapped(): boolean {
        return (
            this.localScrapReturnConsumed === RETURN_CODE.LOCAL_SCRAP ||
            this.localScrapReturnConsumed === RETURN_CODE.CENTRAL_SCRAP
        );
    }

    public hasSmartSensorTracker(): boolean {
        return this.configService.getSmartSensorTracker().includes(this.materialIdentifier.materialNumber.value);
    }

    // smart sensor trackers do have baterry in device, but they are excluded from this logic
    public isLithiumBatteryInDevice(): boolean {
        return !this.hasSmartSensorTracker() && this.configService.isMaterialLiBatteryInDevice(this.unNumber);
    }

    public isLithiumBatteryStandAlone(): boolean {
        return this.configService.isMaterialLiBatteryStandAlone(this.unNumber);
    }

    public showServicePartQuestionnaire(): boolean {
        return (
            this.spqDocId.length > 0 &&
            this.configService.getDisplayServicePartQuestionnaire() &&
            (this.consumptionCode.selectedEntry?.value === CONSUME_CODE.CONSUMED ||
                (this.consumptionCode.selectedEntry?.value === CONSUME_CODE.RETURN_WITHOUT_CONSUMPTION &&
                    this.returnReason.selectedEntry?.value === RETURN_REASON_CODE.DEAD_ON_ARRIVAL)) &&
            this.returnType.selectedEntry?.value === RETURN_CODE.CENTRAL_RETURN
        );
    }
}

export class MaterialModelFromJson extends MaterialModel {
    public constructor(data: JsonMaterialModel, notification: NotificationModel | undefined) {
        super(undefined, notification);
        this.materialIdentifier = new MaterialIdentifierModelFromJson(data.materialIdentifier);
        this.notificationIdentifier = new NotificationIdentifierModelFromJson(data.notificationIdentifier);
        this.activityDate.value = data.activityDate ? moment(data.activityDate) : moment.invalid();
        this.statusString.value = data.statusString;
        this.materialKey = data.materialKey;
        this.description.value = data.description;
        this.quantity.value = data.quantity;
        this.quantity.options = {
            ...this.quantity.options,
            maxValue: data.maxQuantity,
        };
        this.maxQuantity = data.maxQuantity;
        this.quantityUnit.value = data.quantityUnit;
        this.faultDescription.value = data.faultDescription;
        this.materialNumberOld.value = data.materialNumberOld;
        this.revisionNumberOld.value = data.revisionNumberOld;
        this.revisionNumberNew.value = data.revisionNumberNew;
        this.installationDateOld.value = data.installationDateOld ? moment(data.installationDateOld) : moment.invalid();
        this.installationDateNew.value = data.installationDateNew ? moment(data.installationDateNew) : moment();
        this.counterNumberOld.value = data.counterNumberOld;
        this.counterNumberNew.value = data.counterNumberNew;
        this.pickedQuantity.value = data.pickedQuantity;
        this.materialStatus.statusCode = data.materialStatus;
        this.materialSource.sourceCode = data.materialSource;
        this.deliveredQuantity = data.deliveredQuantity;
        this.tubeIndicator = data.tubeIndicator;
        this.serialPIndicator = data.serialPIndicator;
        this.batchNumber.value = data.batchNumber;
        this.batchNumberLegacy = data.batchNumberLegacy ? data.batchNumberLegacy : '';
        this.awbDelivery = data.awbDelivery;
        this.ucrNumber = data.ucrNumber ?? '';
        this.unNumber = data.unNumber ?? '';
        this.dangerousGoodsText = data.dangerousGoodsText;
        this.hazardousGoodsText = data.hazardousGoodsText;
        this.dangerousGoodsIndicator = data.dangerousGoodsIndicator ? data.dangerousGoodsIndicator : '';
        this.showDangerousInfo = data.showDangerousInfo && !!(data.dangerousGoodsText || data.hazardousGoodsText);
        this.parentMaterialModelIndex = data.parentMaterialModelIndex;
        this.storageLocation.value = data.storageLocation;
        this.isRepairable = data.isRepairable;
        this.isQualityPart = data.isQualityPart;
        this.spqDocId = data.spqDocId;
        this.isComplaint.selectByPredicate((entry) => {
            return entry.value === data.complaintText;
        });
        this.isBatteryRemoved.selectByPredicate((entry) =>
            typeof data.isBatteryRemoved !== 'undefined'
                ? entry.value === data.isBatteryRemoved
                : entry.value === ConfigConstants.optionNA,
        );
        this.isBatteryDamaged.selectByPredicate((entry) =>
            typeof data.isBatteryDamaged !== 'undefined'
                ? entry.value === data.isBatteryDamaged
                : entry.value === ConfigConstants.optionNA,
        );
        const csmlTrackerLink = this.configService.getCSMLTrackerLink();
        const useCsmlUrlWithParam = this.configService.getUseCsmlUrlWithParam();
        this.csmlTrackerUrl = this.uriUtilities.getCSMLTrackerURL(
            [this.materialIdentifier],
            csmlTrackerLink,
            true,
            useCsmlUrlWithParam,
        );

        // if the status is returned or "not received" then make quantity disabled
        if (
            data.materialStatus === MATERIAL_STATUS_CODE.RETURNED ||
            data.materialStatus === MATERIAL_STATUS_CODE.NOTRECEIVED
        ) {
            this.quantity.options = {
                ...this.quantity.options,
                readonly: true,
            };
        }

        data.consumptionCode.forEach((jsonValue) => {
            this.consumptionCode.selectByPredicate((entry) => entry.value === jsonValue);
        });
        data.phiDataCode.forEach((jsonValue) => {
            this.phiDataCode.selectByPredicate((entry) => entry.value === jsonValue);
        });
        // Migration logic to fetch billing indicator values if doesn't exist in app
        // for example, user opened the app before this fix
        if (isDefined(data.billing)) {
            this.billing = data.billing;
            this.billingChangeable = data.billingChangeable;
        } else {
            if (data.materialSource === MATERIAL_SOURCE_CODE.OTHER) {
                data.billableCode.forEach((jsonValue) => {
                    this.billableCode.selectByPredicate((entry) => entry.value === jsonValue);
                });
                this.billing = <ConfigConstants.BILLABLE_CODE>this.configService.getAddMaterialBillingIndicator();
                this.billingChangeable = this.configService.getAddMaterialBillingChangeable();
            } else {
                notification?.partSet.map((part) => {
                    if (
                        NotificationIdentifierModel.compare(this.notificationIdentifier, notification?.identifier) &&
                        MaterialIdentifierModel.compare(part.materialIdentifier, this.materialIdentifier)
                    ) {
                        this.billing = part.billing;
                        this.billingChangeable = part.billingChangeable;
                    }
                });
            }
        }

        data.billableCode.forEach((jsonValue) => {
            this.billableCode.selectByPredicate((entry) => entry.value === jsonValue);
        });
        this.billableCode.options = {
            ...this.billableCode.options,
            disabled: data.billableCodeDropdownDisabled,
            required: data.billableCodeDropdownRequired,
        };
        // fault code does not get MaterialErrorI or MaterialErrorJ values since we pass part set as null so tube indicator remains undefined
        this.faultCode.value.length = 0;
        this.faultCode.addModels(this.getFaultCodeOptions());

        data.faultCode.forEach((jsonValue) => {
            this.faultCode.selectByPredicate((entry) => entry.value === jsonValue);
        });

        this.lsCrChangeable = data.lsCrChangeable;
        this.localScrapReturnConsumed = data.localScrapReturnConsumed;
        this.localScrapReturnWithoutConsumption = data.localScrapReturnWithoutConsumption;
        this.localScrapReturnLeftOnNotification = data.localScrapReturnLeftOnNotification;
        this.localScrapReturnNotReceived = data.localScrapReturnNotReceived;
        this.amount.value = data.amount;
        this.repairPrice = data.repairPrice ?? 0;
        this.currency = data.currency;
        this.customerPrice = data.customerPrice ?? 0;
        this.customerCurrency = data.customerCurrency ?? '';
        this.textLine = data.textLine;
        this.isRentalTool = data.isRentalTool ?? this.isRentalMaterial();
        this.setRentalToolOptions();
        this.originalSerialNumberOld = data.originalSerialNumberOld;
        this.originalSerialNumberNew = data.originalSerialNumberNew;
        this.setSerialNumberOldField();
        // Migration logic for Serial number text box
        if (this.serialNumberDropdown) {
            const serialNumberOld = isDefined(data.serialNumberOld)
                ? data.serialNumberOld
                : data.serialNumberOldDropdown;
            if (this.serialNumberDropdown.options.displayText) {
                this.serialNumberDropdown.textFieldValue = serialNumberOld;
            } else {
                this.serialNumberDropdown.selectByPredicate((entry) => entry.value === data.serialNumberOldDropdown);
            }
        }
        this.serialNumberNew.value = data.serialNumberNew;
        if (this.consumptionCode && this.consumptionCode.selectedEntry) {
            this.setReturnTypeOptions(this.consumptionCode.selectedEntry.model.value);
        }

        data.returnType.forEach((value) => {
            this.returnType.selectByPredicate((entry) => {
                return entry.value === value;
            });
        });
        this.loadAWBReturnFieldProperties();
        const awbReturnValue = data.awbReturn || data.ucrNumber;
        this.awbReturn.value = this.hasSmartSensorTracker() ? '' : awbReturnValue;
        data.returnReason.forEach((value) => {
            this.returnReason.selectByPredicate((entry) => entry.value === value);
        });
        this.isConsumed = data.isConsumed;
        if (data.dopAddress) {
            this.dopAddress = new CseDopModelFromJson(data.dopAddress);
        }
        this.deliveryReturnSelectedOption = data.deliveryReturnSelectedOption;
        this.dropOffPointSelectedOption = data.dropOffPointSelectedOption;
        this.returnInfoText = data.returnInfoText;
        this.awbPackagingText = data.awbPackagingText;
        this.isRowSelected.value = data.isRowSelected;
        this.materialReturnedDate = data.materialReturnedDate;
        this.noPhysicalReturn = data.noPhysicalReturn;
        this.showOnNotificationDetail = data.showOnNotificationDetail;
        this.objInfoDGPText = data.objInfoDGPText;
        this.isActive = data.isActive;
        if (data.customerAddress) {
            this.customerAddress = new AddressModelFromJson(data.customerAddress);
        }
        this.orderPriority = data.orderPriority;
        this.orderInfoText = data.orderInfoText;
        this.smartSensorTrackerUrl = this.hasSmartSensorTracker() ? this.configService.getSmartSensorTrackerUrl() : '';
        if (data.isPurchaseOrderTaxiBillable) {
            this.isPurchaseOrderTaxiBillable = data.isPurchaseOrderTaxiBillable;
        } else if (notification && this.materialIdentifier.orderNumber.value) {
            this.isPurchaseOrderTaxiBillable = this.isMaterialTaxiBillable(
                notification,
                this.materialIdentifier.orderNumber.value.toString(),
            );
        } else {
            this.isPurchaseOrderTaxiBillable = false;
        }
        this.isMaterialConsumptionAdditionalTaxiCost = data.isMaterialConsumptionAdditionalTaxiCost;
        this.setReturnReasonOptions();
        this.lastSavedTimestamp = data.lastSavedTimestamp ?? 0;
        this.isFDA2579FormEnabled = data.isFDAFormEnabled;
        if (this.isFDA2579FormEnabled && notification) {
            this.fda2579FormModel =
                typeof data.fda2579FormModel !== 'undefined'
                    ? new Fda2579FormModelFromJson(data.fda2579FormModel, notification)
                    : new Fda2579FormModel(notification);
        }

        this.doNotReplenishBootstockMaterial.value = data.doNotReplenishBootstockMaterial;
        this.isTrunkStockMaterial = data.isTrunkStockMaterial;
        this.isBootstockReturn = data.isBootstockReturn ?? false;
        this.isBootStockScrapable = data.isBootStockScrapable ?? false;
        this.isMedPhysicsRelevant = data.isMedPhysicsRelevant ?? false;
        // Compute isBootstockMaterial from materialSource if not provided in JSON (for backward compatibility)
        this.isBootstockMaterial =
            data.isBootstockMaterial ?? this.materialSource.sourceCode === MATERIAL_SOURCE_CODE.BOOTSTOCK;
        this.isSpqSubmitted = data.isSpqSubmitted ?? false;
    }
}

export interface JsonMaterialModel {
    materialIdentifier: JsonMaterialIdentifierModel;
    notificationIdentifier: JsonNotificationIdentifierModel;
    materialKey: string;
    description: string;
    maxQuantity: number;
    awbReturn: string;
    deliveredQuantity: number;
    quantity: number;
    quantityUnit: string;
    faultDescription: string;
    materialNumberOld: string;
    serialNumberOld: string;
    serialNumberOldDropdown: string;
    serialNumberNew: string;
    revisionNumberOld: string;
    revisionNumberNew: string;
    installationDateOld: moment.Moment;
    installationDateNew: moment.Moment;
    counterNumberOld: number;
    counterNumberNew: number;
    materialStatus: MATERIAL_STATUS_CODE;
    materialSource: MATERIAL_SOURCE_CODE;
    pickedQuantity: number;
    consumptionCode: string[];
    phiDataCode: string[];
    billableCode: string[];
    billableCodeDropdownDisabled: boolean;
    billableCodeDropdownRequired: boolean;
    faultCode: string[];
    returnType: string[];
    returnReason: string[];
    tubeIndicator: string;
    serialPIndicator: string;
    dangerousGoodsIndicator: string;
    showDangerousInfo: boolean;
    batchNumber: string;
    awbDelivery: string;
    ucrNumber: string;
    unNumber: string;
    dangerousGoodsText: string;
    hazardousGoodsText: string;
    parentMaterialModelIndex: number;
    storageLocation: string;
    complaintText: string;
    isBatteryRemoved: string;
    isBatteryDamaged: string;
    activityDate: moment.Moment;
    statusString: string;
    isConsumed: boolean;
    dopAddress: JsonCseDopModel;
    deliveryReturnSelectedOption: string;
    dropOffPointSelectedOption: string;
    returnInfoText: string;
    awbPackagingText: string;
    isRowSelected: boolean;
    materialReturnedDate: string;
    noPhysicalReturn: boolean;
    showOnNotificationDetail: boolean;
    objInfoDGPText: string;
    lsCrChangeable: string;
    localScrapReturnConsumed: string;
    localScrapReturnWithoutConsumption: string;
    localScrapReturnLeftOnNotification: string;
    localScrapReturnNotReceived: string;
    amount: number;
    repairPrice: number;
    currency: string;
    customerPrice: number;
    customerCurrency: string;
    textLine: string;
    isActive: boolean;
    customerAddress: AddressState;
    orderPriority: string;
    orderInfoText: string;
    originalSerialNumberOld: string;
    originalSerialNumberNew: string;
    isPurchaseOrderTaxiBillable: boolean;
    isMaterialConsumptionAdditionalTaxiCost: boolean;
    isRepairable: boolean;
    isQualityPart: boolean;
    batchNumberLegacy: string;
    spqDocId: string;
    isRentalTool: boolean;
    lastSavedTimestamp: number;
    billing: ConfigConstants.BILLABLE_CODE;
    billingChangeable: boolean;
    isFDAFormEnabled: boolean;
    fda2579FormModel: JsonFda2579FormModel;
    doNotReplenishBootstockMaterial: boolean;
    isTrunkStockMaterial: boolean;
    isBootstockReturn: boolean;
    isBootStockScrapable: boolean;
    isBootstockMaterial?: boolean;
    isSpqSubmitted: boolean;
    isMedPhysicsRelevant?: boolean;
}

export interface MaterialOutModel extends ODataMaterialIdentifierOutModel {
    BatchNumber: string;
    Quantity: string;
    UOM: string;
    ShortText: string;
    OldMaterial: string;
    AirwayBillNo: string;
    OldSerialNumber: string;
    SerialNumber: string;
    MaterialErrorCode: string;
    RevisionNeu: string;
    OperationDate: string;
    DefectTubeRemovalDate: string;
    CounterX: string;
    Counter: string;
    Billable: string;
    LsCrIndicator: string;
    ReasonCode: string;
    MaterialSource: string;
    StorageLocation: string;
    MKCode: string;
    MaterialBugs: string;
    Matbez: string;
    Preis: string;
    FDA2579Form: string;
    Herkunft: string;
}
